import { Controller } from '@hotwired/stimulus'
import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  offset,
  size
} from '@floating-ui/dom'

const DEFAULT_OFFSET_PIXEL = 8
const FIXED_HEADER_CLEARANCE = 64
const MENU_POSITION_PROPERTIES = ['position', 'top', 'left', 'z-index', 'width', 'max-width', 'max-height']

// Tracks keep-open dropdowns that are open across morphs. A Turbo Stream morph can
// recreate the wrapper element (rather than morphing it in place), which disconnects
// and reconnects the controller with a fresh state. Keying by the wrapper id lets a
// reconnecting controller restore its open state.
const openKeepAliveDropdownIds = new Set()
const openDropdowns = new Set()

// Connects to data-controller="dropdown-floating"
export default class extends Controller {
  static targets = ['button', 'menu', 'display']
  static values = {
    position: { type: String, default: 'bottom-start' },
    maxHeight: { type: Number, default: null },
    offsetPixel: { type: Number, default: DEFAULT_OFFSET_PIXEL },
    keepOpenAfterMorph: { type: Boolean, default: false },
    strategy: { type: String, default: 'absolute' }
  }

  connect () {
    this.isOpen = false

    // Fired on full-page refresh morphs (turbo_refreshes_with method: :morph).
    this.handleMorph = () => this.#restoreOpenState()

    // Turbo Stream morphs (turbo_stream.replace(..., method: :morph)) never fire
    // turbo:morph, only the per-element turbo:before-morph-element (cancelable) and
    // turbo:morph-element events. Skipping the open menu's morph keeps it visible and
    // positioned without flicker; restoring on the wrapper's morph-element is a safety
    // net in case idiomorph still touches the menu.
    this.handleBeforeMorphElement = (event) => {
      if (!this.isOpen || !this.keepOpenAfterMorphValue || !this.hasMenuTarget) return
      if (event.target === this.menuTarget) event.preventDefault()
    }

    this.handleMorphElement = (event) => {
      if (event.target === this.element) this.#restoreOpenState()
    }

    this.handleFrameLoad = () => {
      if (this.isOpen) {
        this.updatePosition()
      }
    }

    document.addEventListener('turbo:morph', this.handleMorph)
    document.addEventListener('turbo:before-morph-element', this.handleBeforeMorphElement)
    document.addEventListener('turbo:morph-element', this.handleMorphElement)
    document.addEventListener('turbo:frame-load', this.handleFrameLoad)

    if (this.hasMenuTarget) {
      this.menuTarget.addEventListener('change', this.handleSelection)
    }

    if (this.#shouldRestoreOpenOnConnect) this.open()
  }

  get #shouldRestoreOpenOnConnect () {
    return this.keepOpenAfterMorphValue &&
      this.element.id &&
      openKeepAliveDropdownIds.has(this.element.id)
  }

  disconnect () {
    document.removeEventListener('turbo:morph', this.handleMorph)
    document.removeEventListener('turbo:before-morph-element', this.handleBeforeMorphElement)
    document.removeEventListener('turbo:morph-element', this.handleMorphElement)
    document.removeEventListener('turbo:frame-load', this.handleFrameLoad)

    if (this.hasMenuTarget) {
      this.menuTarget.removeEventListener('change', this.handleSelection)
    }

    // Release resources without mutating the keep-alive registry, so a reconnecting
    // controller (recreated by a morph) can still restore its open state.
    openDropdowns.delete(this)
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate()
      this.cleanupAutoUpdate = null
    }
    document.removeEventListener('click', this.handleClickOutside)
  }

  #restoreOpenState () {
    if (!this.isOpen) return

    if (this.keepOpenAfterMorphValue) {
      this.open()
    } else {
      this.close()
    }
  }

  toggle (event) {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open () {
    this.#closeOthers()

    this.isOpen = true
    this.menuTarget.removeAttribute('hidden')
    if (this.cleanupAutoUpdate) this.cleanupAutoUpdate()
    this.cleanupAutoUpdate = autoUpdate(
      this.buttonTarget,
      this.menuTarget,
      () => this.updatePosition()
    )

    if (this.keepOpenAfterMorphValue && this.element.id) {
      openKeepAliveDropdownIds.add(this.element.id)
    }

    openDropdowns.add(this)
    document.addEventListener('click', this.handleClickOutside)
    this.dispatch('opened')
  }

  close () {
    if (!this.isOpen) return

    this.isOpen = false
    this.menuTarget.setAttribute('hidden', '')
    this.#resetMenuPosition()

    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate()
      this.cleanupAutoUpdate = null
    }

    if (this.element.id) openKeepAliveDropdownIds.delete(this.element.id)

    openDropdowns.delete(this)
    document.removeEventListener('click', this.handleClickOutside)
    this.dispatch('closed')
  }

  handleClickOutside = (event) => {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  handleSelection = (event) => {
    const input = event.target
    if (input.type !== 'radio' || !input.checked || !this.hasDisplayTarget) return

    const content = input
      .closest('label')
      ?.querySelector('[data-dropdown-floating-label-content]')

    if (content) {
      this.displayTarget.replaceChildren(...content.cloneNode(true).childNodes)
    } else {
      const labelText = input.dataset.dropdownFloatingLabel?.trim()
      if (!labelText) return

      this.displayTarget.textContent = labelText
    }

    this.close()
  }

  updatePosition () {
    const maxHeight = this.maxHeightValue
    const boundaryPadding = {
      top: FIXED_HEADER_CLEARANCE,
      right: this.offsetPixelValue,
      bottom: this.offsetPixelValue,
      left: this.offsetPixelValue
    }
    computePosition(this.buttonTarget, this.menuTarget, {
      strategy: this.strategyValue,
      placement: this.positionValue,
      middleware: [
        flip({ padding: boundaryPadding }),
        shift({ padding: boundaryPadding }),
        offset(this.offsetPixelValue),
        size({
          padding: boundaryPadding,
          apply ({ availableWidth, availableHeight, elements }) {
            if (maxHeight) {
              availableHeight = Math.min(availableHeight, maxHeight)
            }
            Object.assign(elements.floating.style, {
              maxWidth: `${Math.max(0, availableWidth)}px`,
              maxHeight: `${Math.max(0, availableHeight)}px`
            })
          }
        })
      ]
    }).then(({ x, y, strategy }) => {
      Object.assign(this.menuTarget.style, {
        position: strategy,
        top: `${y}px`,
        left: `${x}px`,
        zIndex: '99999'
      })
    })
  }

  #resetMenuPosition () {
    if (!this.hasMenuTarget) return

    const { style } = this.menuTarget
    for (const property of MENU_POSITION_PROPERTIES) {
      style.removeProperty(property)
    }
  }

  #closeOthers () {
    for (const dropdown of [...openDropdowns]) {
      if (dropdown !== this) dropdown.close()
    }
  }
}
